import {
  clearStorage,
  getUserToken,
  removeItem,
  setItem,
  updateTokenStorage,
} from "@/services/storage-data";
import config from "@/config";
import {
  clearLegacyUiStylesheetStorage,
  isHpClientTheme,
  stylesheetFromPreferences,
  uiStylesheetToTheme,
  useTheme,
} from "@/contexts/theme";
import { systemCurrencyState, systemVATState, userState } from "@/store";
import { permissionsState } from "@/store/permissions";
import { linesOfBusinessState } from "@/store/lines-of-business";
import {
  getEffectivePermissions,
  hasPermission,
  normalizeSessionPermissions,
} from "@/utils/session-permissions";
import { useCallback, useState } from "react";
import { useRecoilCallback, useRecoilState, useSetRecoilState } from "recoil";
import { useGomakeAxios } from "./use-gomake-axios";
import { useGomakeRouter } from "./use-gomake-router";
import { userTypeState } from "@/store/user-type";
import { userProfileState } from "@/store/user-profile";
import { useTranslation } from "react-i18next";
import { Permissions } from "@/components/CheckPermission/enum";
import {
  GOMAKE_AI_FEATURE_ID,
  GOMAKE_MIS_FEATURE_ID,
  isUsPrintHouseState,
  printHouseProfile,
} from "@/store/print-house-profile";
import { priceChangePercentsState } from "@/store/min-max-price-change-percents";
import { printHousePreferencesState } from "@/store/print-house-preferences";
import { versionState } from "@/store/version";
import { printHouseCountryState } from "@/store/print-house-country";
import { setupGuideOpenState } from "@/store/setup-guide-state";
import { getPrintHouseSetupStepsApi } from "@/services/api-service/quick-setup-menu/set-up-api";
import { resetLoginRecaptcha } from "@/widgets/login/login-recaptcha";
import {
  printHouseResetPasswordModalState,
  printHouseSetupStepsState,
  printHouseWelcomeModalState,
  SetupStep,
  SetupStepItem,
  StepStatus,
} from "@/store/print-house-setup-state-state";
import { isLoggedInState } from "@/store/token-expired-modal";
import {
  clearValidateSnapshot,
  getValidateSnapshot,
  setValidateSnapshot,
} from "@/services/auth/validate-cache";
import { setSessionPrintHouseId } from "@/services/auth/session-print-house-id";
import { getTimeZoneOffsetHours } from "@/services/api-service/auth/oidc-api";

const useCustomer = (
  permissionEnumValue?: Permissions,
  allowAnonymous?: boolean,
) => {
  const { callApi } = useGomakeAxios();
  const setPrintHouseSetupSteps = useSetRecoilState<SetupStepItem[]>(
    printHouseSetupStepsState,
  );
  const setPrintHouseWelcomeModal = useSetRecoilState<boolean>(
    printHouseWelcomeModalState,
  );

  const setResetPasswordModal = useSetRecoilState<boolean>(
    printHouseResetPasswordModalState,
  );
  const [user, setUser] = useRecoilState<any>(userState);
  const setSystemCurrency = useSetRecoilState<any>(systemCurrencyState);
  const setSystemVAT = useSetRecoilState<number>(systemVATState);
  const setUserProfile = useSetRecoilState(userProfileState);
  const setPrintHouseProfile = useSetRecoilState(printHouseProfile);
  const setPrintHouseCountry = useSetRecoilState(printHouseCountryState);

  const setPriceChangePercents = useSetRecoilState(priceChangePercentsState);
  const [isLanguageUpdated, setIsLanguageUpdated] = useState<boolean>(false);
  const setUserType = useSetRecoilState<any>(userTypeState);
  const [adminsAutoComplate] = useState([]);
  const [permissions, setPermissions] = useRecoilState<any>(permissionsState);
  const setLinesOfBusiness = useSetRecoilState(linesOfBusinessState);
  const { navigate } = useGomakeRouter();
  const setPrintHousePreferences = useSetRecoilState<any>(
    printHousePreferencesState,
  );
  const setIsUsPrintHouse = useSetRecoilState<any>(isUsPrintHouseState);
  const setVersionState = useSetRecoilState(versionState);
  const setShowSetupMenu = useSetRecoilState(setupGuideOpenState);
  const setIsLoggedIn = useSetRecoilState(isLoggedInState);
  const { i18n } = useTranslation();
  const { setTheme } = useTheme();
  const logOut = useCallback(() => {
    resetLoginRecaptcha();
    clearValidateSnapshot();
    setIsLoggedIn(false);
    setUser({});
    clearStorage();
    navigate("/");
  }, [navigate, setIsLoggedIn, setUser]);

  const validate = useCallback(async () => {
    // if (allowAnonymous ) {
    //   return true;
    // }
    // A token that's present but rejected means we must sign the user out of Cognito before
    // re-login (a silent re-authorize would re-issue the same rejected identity and loop).
    const hadToken = !!getUserToken();
    const cachedValidate = getValidateSnapshot<any>();
    const validate: any =
      cachedValidate ??
      (await callApi("GET", "/v1/auth/validate", {
        timeZoneOffset: getTimeZoneOffsetHours(),
      }));
    if (validate?.success) {
      if (!cachedValidate) {
        setValidateSnapshot(validate);
      }
      const user = validate?.data?.data?.customer;
      if (user?.printHouseId) {
        setSessionPrintHouseId(String(user.printHouseId));
      }
      updateTokenStorage(user?.token);
      // Runs on fresh responses and on 30-minute snapshot hits alike (the snapshot keeps the raw payload).
      const userPermissions = normalizeSessionPermissions(user.permissions);
      user.permissions = null;
      const userLinesOfBusiness = Array.isArray(user.linesOfBusiness)
        ? user.linesOfBusiness
        : [];
      delete user.linesOfBusiness;
      if (user?.isFirstLogin) {
        // setStartGuid(true);
        // localStorage.setItem("isHover", "true");
      }
      setUser({ ...user, type: "user" });
      setUserType({ type: "user" });
      setUserProfile(user);
      setIsLoggedIn(true);
      setPrintHouseProfile(user.printHouseProfile);
      setPrintHouseCountry(user?.printHouseProfile?.country);
      setIsUsPrintHouse(user.printHouseProfile?.country === "US");
      setPrintHousePreferences(user?.printHouseProfile?.preferences);
      setSystemCurrency(user.printHouseProfile.systemCurrency);
      setSystemVAT(user.systemVat);
      //setVersionState(user.appVersion);
      setShowSetupMenu(user?.showSetupMenu);
      const appVersion = user.appVersion;
      const userAppVersion = localStorage.getItem("appVersion");
      if (!userAppVersion) {
        localStorage.setItem("appVersion", appVersion);
      }
      if (userAppVersion && appVersion && userAppVersion !== appVersion) {
        localStorage.setItem("appVersion", appVersion);
        window.location.reload();
      }
      setPriceChangePercents({
        minPercent: user?.minPriceChangePercents,
        maxPercent: user?.maxPriceChangePercents,
      });
      localStorage.setItem(
        "systemLogo",
        validate?.data?.data?.customer?.printHouseProfile?.logo,
      );
      const printHouseTheme =
        typeof user?.printHouseProfile?.theme === "string"
          ? user.printHouseProfile.theme.trim()
          : "";

      const isHpPrintHouse =
        config.hp_theme === "true" ||
        isHpClientTheme(
          printHouseTheme,
          (user?.printHouseProfile as { tenantId?: string | null })?.tenantId,
        );

      if (isHpPrintHouse) {
        // HP customers always get the HP brand theme, untouched by the stylesheet preference.
        setItem("selectedTheme", "hp-client");
        setTheme("hp-client");
      } else if (printHouseTheme) {
        setItem("selectedTheme", printHouseTheme);
        setTheme(printHouseTheme);
      } else {
        // No brand theme: the print house's UI_STYLESHEET preference decides (modern by default).
        clearLegacyUiStylesheetStorage();
        removeItem("selectedTheme");
        setTheme(
          uiStylesheetToTheme(
            stylesheetFromPreferences(user?.printHouseProfile?.preferences),
          ),
        );
      }

      if (validate?.data?.data?.customer?.systemLang) {
        localStorage.setItem(
          "systemLanguage",
          validate?.data?.data?.customer?.systemLang,
        );
        i18n
          .changeLanguage(validate?.data?.data?.customer?.systemLang, () =>
            setIsLanguageUpdated(true),
          )
          .then();
      }

      const hasMISFeature =
        user?.printHouseProfile?.allowedFeaturesId?.includes(
          GOMAKE_MIS_FEATURE_ID,
        ) ?? false;

      const hasAIFeature =
        user?.printHouseProfile?.allowedFeaturesId?.includes(
          GOMAKE_AI_FEATURE_ID,
        ) ?? false;

      if (hasAIFeature && !hasMISFeature) {
        const currentPath = window.location.pathname;

        // If billing is required (not paid), redirect to billing page
        if (
          user?.isGoAiBillingRequired &&
          currentPath !== "/gomake-ai/billing"
        ) {
          navigate("/gomake-ai/billing");
          return true;
        }

        // If billing is not required (paid), restrict to AI page only
        if (!user?.isGoAiBillingRequired && currentPath !== "/gomake-ai") {
          navigate("/gomake-ai");
          return true;
        }
      }

      if (!!user?.redirectTo) {
        navigate(user?.redirectTo);
      }
      setPermissions(userPermissions);
      setLinesOfBusiness(userLinesOfBusiness);
      if (permissionEnumValue !== null && permissionEnumValue !== undefined) {
        if (userPermissions) {
          // Gate on the permissions of the currently selected lines of business.
          const isPermission: boolean = hasPermission(
            getEffectivePermissions(userPermissions, userLinesOfBusiness),
            permissionEnumValue,
          );
          if (!isPermission) {
            //navigate("/login");
            redirectToLogin(false);
          }

          return isPermission;
        } else {
          return false;
        }
      }
      return true;
    }
    clearValidateSnapshot();
    clearStorage();

    if (allowAnonymous) {
      return;
    }
    setIsLoggedIn(false);
    // navigate("/login");
    redirectToLogin(true, hadToken);

    return false;
  }, [setTheme]);

  const checkPermission = useCallback(
    (permission: string) => {
      let canAccess = false;
      const index = permissions.findIndex(
        (item: any) => item.route === permission,
      );
      if (index !== -1) {
        canAccess = true;
      }
      return canAccess;
    },
    [permissions],
  );

  const getPermission = useCallback(
    (permission: string) => {
      const _permissions = permissions.filter(
        (item: any) => item.route === permission,
      );
      let op = "";
      if (
        _permissions[0]?.op === "read" ||
        _permissions[1]?.op === "read" ||
        _permissions[2]?.op === "read"
      ) {
        op = "read";
      }
      if (
        _permissions[0]?.op === "write" ||
        _permissions[1]?.op === "write" ||
        _permissions[2]?.op === "write"
      ) {
        op = "write";
      }
      if (
        _permissions[0]?.op === "admin" ||
        _permissions[1]?.op === "admin" ||
        _permissions[2]?.op === "admin"
      ) {
        op = "admin";
      }
      return { op };
    },
    [permissions],
  );

  const getPrintHouseSetupSteps = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const profile = snapshot.getLoadable(printHouseProfile).getValue();
        if (profile?.isShowOnBoardingMenu === false) {
          return;
        }

        const callBack = (res) => {
          if (res.success) {
            const steps: SetupStepItem[] = res?.data;
            setPrintHouseSetupSteps(steps);

            // const welcomeStep = steps.find(
            //   (step) => step.step === SetupStep.WELCOME_MESSAGE
            // );
            // if (welcomeStep.status === StepStatus.NOT_YET) {
            //   setPrintHouseWelcomeModal(true);
            // }

            const resetPassStep = steps.find(
              (step) => step.step === SetupStep.RESET_PASSWORD,
            );
            if (resetPassStep.status === StepStatus.NOT_YET) {
              setResetPasswordModal(true);
            }
          }
        };

        await getPrintHouseSetupStepsApi(callApi, callBack);
      },
    [callApi],
  );

  const redirectToLogin = (withReturnUrl: boolean, sessionExpired = false) => {
    //let domain = window.location.origin;
    let domain = window.location.hostname;
    let loginPageName = "login";
    if (domain === "ai.gomake.net") {
      loginPageName = "gomake-ai";
    }
    // Tells the login redirector to sign out of Cognito first (breaks the re-auth loop that a
    // rejected-but-present token would otherwise cause).
    const params = new URLSearchParams();
    if (withReturnUrl) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== "/") {
        params.set("redirect", currentPath);
      }
    }
    if (sessionExpired) {
      params.set("sessionExpired", "1");
    }
    const query = params.toString();
    navigate(query ? `/${loginPageName}?${query}` : `/${loginPageName}`);
  };
  return {
    adminsAutoComplate,
    user,
    setUser,
    logOut,
    checkPermission,
    getPermission,
    setPermissions,
    validate,
    isLanguageUpdated,
    getPrintHouseSetupSteps,
  };
};

export { useCustomer };
